import { notification } from 'antd';
import type { AxiosError } from 'axios';
import axios from 'axios';
import type { Dayjs } from 'dayjs';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { ErrorMessage } from '../../../../../../models/error';
import type { AsyncThunkConfig } from '../../../../../../models/slice';
import { RaygunErrorHandlerService } from '../../../../../../service/raygun.service';
import { fetchClientEvents } from '../submissionSlice';

const { logError } = RaygunErrorHandlerService();

type Emoji = {
  cldr_short_name: string;
  hex_code: string;
  id: string;
  name: number;
  unicode: string;
};

type ClientClinician = {
  clinician_id: string;
  clinician_first_name: string;
  clinician_last_name: string;
  clinician_last_first_name: string;
  assigned: boolean;
};

export type EntriesTabKey = 'feedback' | 'intervention';

type ClientEntriesSliceType = {
  emojis: Emoji[];
  loadingEmojis: boolean;
  loadingSendFeedback: boolean;
  sendFeedbackParam: Feedback;
  loadingSendIntervention: boolean;
  intervention: Intervention;
  loadingClientClinicians: boolean;
  clientClinicians: ClientClinician[];
  activeTab: EntriesTabKey;
  showEntriesModal: boolean;
  isFormDirty: boolean;
};

export class Feedback {
  custom_message: string | null = null;
  emoji_id: string | null = null;
  client_id: string | null = null;
}

export class Intervention {
  client_id: string | null = null;
  clinician_id: string | null = null;
  details: string | null = null;
  severity: string | null = null;
  timestamp: Dayjs | null = null;
}

const initialState: ClientEntriesSliceType = {
  emojis: [],
  loadingEmojis: false,
  loadingSendFeedback: false,
  sendFeedbackParam: new Feedback(),
  clientClinicians: [],
  intervention: new Intervention(),
  loadingClientClinicians: false,
  loadingSendIntervention: false,
  showEntriesModal: false,
  activeTab: 'feedback',
  isFormDirty: false,
};

export const fetchEmojis = createAsyncThunk<Emoji[], undefined, AsyncThunkConfig>(
  'clientEntriesModal/fetchEmojis',
  async (_, thunkAPI) => {
    try {
      const response = (await axios.get('v3_get_feedback_emoji', { signal: thunkAPI.signal })) as Emoji[];
      return response ?? [];
    } catch (e) {
      logError(e, ['clientEntriesModalSlice', 'fetchEmojis']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const sendFeedback = createAsyncThunk<number, Feedback, AsyncThunkConfig>(
  'clientEntriesModal/sendFeedback',
  async (sendFeedbackParam, thunkAPI) => {
    try {
      const response = (await axios.post('v3_send_feedback', sendFeedbackParam)) as number;
      if (sendFeedbackParam.client_id && response != null) {
        thunkAPI.dispatch(fetchClientEvents(sendFeedbackParam.client_id));
        thunkAPI.dispatch(closeEntriesModal());
      }
      return response; // sent_feedback number
    } catch (e) {
      thunkAPI.dispatch(closeEntriesModal());
      logError(e, ['clientEntriesModalSlice', 'sendFeedback']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const sendIntervention = createAsyncThunk<string, Intervention, AsyncThunkConfig>(
  'clientEntriesModal/sendIntervention',
  async (intervention, thunkAPI) => {
    try {
      const response = (await axios.post('v2_intervention_upsert', intervention)) as string;
      if (intervention.client_id && response != null) {
        thunkAPI.dispatch(fetchClientEvents(intervention.client_id));
        thunkAPI.dispatch(closeEntriesModal());
      }
      return response; // intervention_id
    } catch (e) {
      thunkAPI.dispatch(closeEntriesModal());
      logError(e, ['clientEntriesModalSlice', 'sendIntervention']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchClientClinicians = createAsyncThunk<ClientClinician[], string, AsyncThunkConfig>(
  'clientEntriesModal/fetchClientClinicians',
  async (client_id, thunkAPI) => {
    try {
      const response = (await axios.post(
        'v2_client_clinician',
        { client_id: client_id },
        { signal: thunkAPI.signal },
      )) as ClientClinician[];
      return response ?? [];
    } catch (e) {
      logError(e, ['clientEntriesModalSlice', 'fetchClientClinicians']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const clientEntriesModalSlice = createSlice({
  name: 'clientEntriesModal',
  initialState,
  reducers: {
    setShowEntriesModal: (state, action: PayloadAction<boolean>) => {
      state.showEntriesModal = action.payload;
    },
    changeActiveTab: (state, action: PayloadAction<EntriesTabKey>) => {
      state.activeTab = action.payload;
    },
    setIsFormDirty: (state, action: PayloadAction<boolean>) => {
      state.isFormDirty = action.payload;
    },
    closeEntriesModal: (state) => {
      state.showEntriesModal = false;
      state.activeTab = 'feedback';
      state.isFormDirty = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchEmojis.pending, (state, _action) => {
      state.loadingEmojis = true;
    });
    builder.addCase(fetchEmojis.fulfilled, (state, action) => {
      state.emojis = action.payload;
      state.loadingEmojis = false;
    });
    builder.addCase(fetchEmojis.rejected, (state, action) => {
      if (action.error.message !== 'Aborted') {
        state.loadingEmojis = false;
      }
    });
    builder.addCase(sendFeedback.pending, (state, _action) => {
      state.loadingSendFeedback = true;
    });
    builder.addCase(sendFeedback.fulfilled, (state, _action) => {
      state.loadingSendFeedback = false;
      notification.success({
        title: 'Success',
        description: 'Feedback sent successfully.',
      });
    });
    builder.addCase(sendFeedback.rejected, (state) => {
      state.loadingSendFeedback = false;
    });

    builder.addCase(fetchClientClinicians.pending, (state, _action) => {
      state.loadingClientClinicians = true;
    });
    builder.addCase(fetchClientClinicians.fulfilled, (state, action) => {
      state.clientClinicians = action.payload;
      state.loadingClientClinicians = false;
    });
    builder.addCase(fetchClientClinicians.rejected, (state, action) => {
      if (action.error.message !== 'Aborted') {
        state.loadingClientClinicians = false;
      }
    });

    builder.addCase(sendIntervention.pending, (state, _action) => {
      state.loadingSendIntervention = true;
    });
    builder.addCase(sendIntervention.fulfilled, (state, _action) => {
      state.loadingSendIntervention = false;
      notification.success({
        title: 'Success',
        description: 'Clinician note saved successfully',
      });
    });
    builder.addCase(sendIntervention.rejected, (state) => {
      state.loadingSendIntervention = false;
    });
  },
});

export const { setShowEntriesModal, closeEntriesModal, changeActiveTab, setIsFormDirty } =
  clientEntriesModalSlice.actions;
